import {
    LayoutTemplate,
    Code2,
    ShoppingCart,
    Search,
    Zap,
    Palette,
    Smartphone,
    Gauge,
    ShieldCheck,
    Rocket,
    PenTool,
    Layers,
  } from 'lucide-react'
  
  export type Service = {
    icon: typeof Code2
    title: string
    desc: string
    features: string[]
  }
  
  export const services: Service[] = [
    {
      icon: LayoutTemplate,
      title: 'Business Websites',
      desc: 'Conversion-focused marketing sites that tell your story and turn visitors into customers.',
      features: ['Custom design', 'SEO-ready', 'Lightning fast', 'CMS optional'],
    },
    {
      icon: ShoppingCart,
      title: 'APP Development',
      desc: 'We create fast, intuitive, and scalable mobile applications designed to deliver seamless experiences and help your business grow.',
      features: [ 'iOS & Android Apps',
        'Cross-Platform Development',
        'UI/UX Design',
        'App Testing & Optimization',],
    },
    {
      icon: Code2,
      title: 'Dynamic Web Apps',
      desc: 'Interactive dashboards, portals, and SaaS products powered by the MERN stack.',
      features: ['Real-time data', 'User accounts', 'Admin panels', 'API integrations'],
    },
    {
      icon: Palette,
      title: 'UI / UX Design',
      desc: 'Interfaces that feel effortless — researched, prototyped, and polished to the pixel.',
      features: ['Wireframing', 'Prototyping', 'Design systems', 'Usability tests'],
    },
    {
      icon: Search,
      title: 'Digital Marketing',
      desc:' We build powerful digital marketing strategies that increase your online visibility, attract the right audience, and turn visitors into customers.',
      features: [ 'Social Media Marketing',
        'Search Engine Optimization',
        'Google & Social Ads',
        'Content Marketing',],
    },
    {
      icon: Layers,
      title: 'Maintenance & Support',
      desc: 'Ongoing care — updates, backups, security patches, and new features as you grow.',
      features: ['Monthly updates', 'Security patches', 'Bug fixes', 'New features'],
    },
  ]
  
  export type Project = {
    title: string
    category: string
    tag: string
    description: string
    metric: string
    metricLabel: string
    accent: string
  }
  
  export const projects: Project[] = [
    {
      title: 'Lumen Store',
      category: 'E-Commerce',
      tag: 'Fashion retail',
      description: 'A full storefront with 2,000+ products, one-tap checkout, and a live order tracker.',
      metric: '+38%',
      metricLabel: 'conversion lift',
      accent: 'from-brand-500/20 to-brand-700/10',
    },
    {
      title: 'Pulse Dashboard',
      category: 'Web App',
      tag: 'SaaS analytics',
      description: 'Real-time analytics dashboard with custom charts, filters, and team collaboration.',
      metric: '1.2M',
      metricLabel: 'events / day',
      accent: 'from-accent-500/20 to-accent-600/10',
    },
    {
      title: 'Verdant Clinic',
      category: 'Business Site',
      tag: 'Healthcare',
      description: 'Booking-first clinic website with online appointments and a patient portal.',
      metric: '4.9★',
      metricLabel: 'patient rating',
      accent: 'from-emerald-500/20 to-emerald-700/10',
    },
    {
      title: 'Forge Technology',
      category: 'Portfolio',
      tag: 'Creative agency',
      description: 'Award-style portfolio with immersive scroll animations and a case-study CMS.',
      metric: '2x',
      metricLabel: 'inbound leads',
      accent: 'from-fuchsia-500/20 to-fuchsia-700/10',
    },
  ]
  
  export type Step = {
    icon: typeof Zap
    title: string
    desc: string
  }
  
  export const steps: Step[] = [
    {
      icon: PenTool,
      title: 'Discover & Design',
      desc: 'We learn your business, map the user journey, and craft a design that fits your brand.',
    },
    {
      icon: Code2,
      title: 'Build & Integrate',
      desc: 'We engineer a fast, dynamic site with the MERN stack and connect the tools you rely on.',
    },
    {
      icon: Gauge,
      title: 'Test & Optimize',
      desc: 'We test across every device, tune performance, and polish every interaction.',
    },
    {
      icon: Rocket,
      title: 'Launch & Grow',
      desc: 'We ship, monitor, and keep improving — adding features as your business scales.',
    },
  ]
  
  export type Feature = {
    icon: typeof Zap
    title: string
    desc: string
  }
  
  export const features: Feature[] = [
    { icon: Gauge, title: 'Blazing Performance', desc: 'Sub-second loads and 90+ Lighthouse scores, out of the box.' },
    { icon: Smartphone, title: 'Pixel-Perfect Responsive', desc: 'Looks and works flawlessly on mobile, tablet, and desktop.' },
    { icon: ShieldCheck, title: 'Secure by Default', desc: 'Best-practice security on every layer of the stack.' },
    { icon: Zap, title: 'Dynamic & Scalable', desc: 'Built on the MERN stack so it grows with your business.' },
  ]
  
  export type Testimonial = {
    name: string
    role: string
    quote: string
    initials: string
  }
  
  export const testimonials: Testimonial[] = [
    {
      name: 'Aarav Mehta',
      role: '',
      quote: 'Raag rebuilt our store and our sales jumped within a month. The site is fast, beautiful, and easy to manage ourselves.',
      initials: 'AM',
    },
    {
      name: 'Jay Shukla',
      role: '',
      quote: 'They delivered a complex dashboard on a tight deadline and the code quality is exceptional. A true partner, not a vendor.',
      initials: 'JS',
    },
    {
      name: 'Capeesh Paris',
      role: '',
      quote: 'Patient bookings doubled after the new site launched. The online appointment system alone paid for the project.',
      initials: 'CP',
    },
    {
      name: 'Maya Desai',
      role: '',
      quote: 'Every animation feels intentional. Raag understood our brand and translated it into a site that wows our clients.',
      initials: 'MD',
    },
  ]
  
  export type Stat = { value: string; label: string }
  
  export const stats: Stat[] = [
    { value: '10+', label: 'Projects shipped' },
    { value: '10+', label: 'Happy clients' },
    { value: '1 yrs', label: 'Combined experience' },
    { value: '24h', label: 'Avg. response time' },
  ]
  
  export const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Work', href: '#work' },
    { label: 'Process', href: '#process' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ]
  